import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Product } from '@socialflows/shared-lib/src/entities/product.entity';
import {
  areAllVariantsSelected,
  isVariantCombinationInStock,
  getAvailableStock,
} from '@/lib/variant-utils';
import { Check, Minus, Plus, ShoppingBag, X } from 'lucide-react';
import { cn, getStockStatus, resolveImageUrl } from '@/lib/utils';

interface QuickAddDialogProps {
  product: Product | null;
  open: boolean;
  onClose: () => void;
  onAddToCart: (
    product: Product,
    quantity: number,
    selectedVariants?: Record<string, string>
  ) => void;
}

export function QuickAddDialog({ product, open, onClose, onAddToCart }: QuickAddDialogProps) {
  const [selectedVariants, setSelectedVariants] = useState<Record<string, string>>({});
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    if (open && product) {
      setSelectedVariants({});
      setQuantity(1);
      setIsAdding(false);
    }
  }, [open, product]);

  if (!product) return null;

  const hasVariants = product.variants && product.variants.length > 0;
  const allVariantsSelected = hasVariants
    ? areAllVariantsSelected(product, selectedVariants)
    : true;
  const inStock = hasVariants
    ? allVariantsSelected && isVariantCombinationInStock(product, selectedVariants)
    : true;
  const availableStock =
    hasVariants && allVariantsSelected ? getAvailableStock(product, selectedVariants) : 0;

  const stockStatus = getStockStatus(availableStock);

  const handleVariantChange = (variantLabel: string, value: string) => {
    setSelectedVariants((prev) => ({
      ...prev,
      [variantLabel]: value,
    }));
  };

  const handleAddToCart = () => {
    if (!allVariantsSelected || !inStock) return;

    setIsAdding(true);

    setTimeout(() => {
      if (hasVariants) {
        onAddToCart(product, quantity, selectedVariants);
      } else {
        onAddToCart(product, quantity);
      }
      onClose();
    }, 400);
  };

  const canAddToCart = allVariantsSelected && inStock && !isAdding;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 p-1 rounded-full bg-background/80 backdrop-blur-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex flex-col sm:flex-row">
          {/* Product Image */}
          <div className="relative w-full sm:w-48 h-48 sm:h-auto bg-muted/30 flex-shrink-0">
            {product.media?.[0]?.url && (
              <img
                src={resolveImageUrl(product.media[0].url)}
                alt={product.label}
                className="absolute inset-0 object-cover"
              />
            )}
            {product.compareAtPrice && product.compareAtPrice > product.price && (
              <span className="absolute top-3 left-3 px-2 py-1 text-xs font-medium bg-red-500 text-white">
                Sale
              </span>
            )}
          </div>

          {/* Product Details */}
          <div className="flex-1 p-5 flex flex-col">
            {/* Header */}
            <div className="mb-4">
              <h2 className="text-lg font-medium leading-tight pr-6">{product.label}</h2>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-xl font-semibold">{product.price?.toFixed(2)} TND</span>
                {product.compareAtPrice && product.compareAtPrice > product.price && (
                  <span className="text-sm text-muted-foreground line-through">
                    {product.compareAtPrice.toFixed(2)} TND
                  </span>
                )}
              </div>
            </div>

            {/* Options */}
            <div className="space-y-4 flex-1">
              {hasVariants &&
                product.variants.map((variant) => (
                  <div key={variant.id}>
                    <label className="text-sm font-medium flex items-center gap-2 mb-2">
                      {variant.label}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        key={variant.id}
                        onClick={() => handleVariantChange('variant', variant.id)}
                        className={cn(
                          'min-w-[2.5rem] h-10 px-3 text-sm border transition-all',
                          selectedVariants['variant'] === variant.id
                            ? 'border-foreground bg-foreground text-background'
                            : 'border-border hover:border-foreground'
                        )}
                        disabled={variant.stock === 0}
                      >
                        {variant.label}
                        {variant.stock === 0 && (
                          <span className="ml-2 text-muted-foreground">(Out of stock)</span>
                        )}
                      </button>
                    </div>
                  </div>
                ))}

              {/* Stock Status */}
              {allVariantsSelected && (
                <div
                  className={cn(
                    'text-sm font-medium',
                    stockStatus.variant === 'success' && 'text-green-600',
                    stockStatus.variant === 'warning' && 'text-yellow-600',
                    stockStatus.variant === 'error' && 'text-red-600'
                  )}
                >
                  {stockStatus.label}
                </div>
              )}
            </div>

            {/* Quantity & Add to Cart */}
            <div className="mt-4 pt-4 border-t space-y-3">
              {/* Quantity */}
              {inStock && allVariantsSelected && (
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium">Qty:</span>
                  <div className="flex items-center border border-border">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label="Decrease quantity"
                    >
                      <Minus className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                    <span className="w-10 text-center text-sm font-medium">{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min(availableStock, quantity + 1))}
                      className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label="Increase quantity"
                    >
                      <Plus className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              )}

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                disabled={!canAddToCart}
                className={cn(
                  'w-full h-12 flex items-center justify-center gap-2 text-sm font-medium transition-all duration-300',
                  isAdding
                    ? 'bg-green-600 text-white'
                    : canAddToCart
                      ? 'bg-foreground text-background hover:bg-foreground/90'
                      : 'bg-muted text-muted-foreground cursor-not-allowed'
                )}
              >
                {isAdding ? (
                  <>
                    <Check className="h-4 w-4 animate-in zoom-in-50 duration-300" strokeWidth={2} />
                    Added!
                  </>
                ) : (
                  <>
                    <ShoppingBag className="h-4 w-4" strokeWidth={1.5} />
                    {!allVariantsSelected
                      ? 'Select Options'
                      : !inStock
                        ? 'Out of Stock'
                        : 'Add to Cart'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Hook for managing quick add dialog state
export function useQuickAdd() {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const openQuickAdd = (product: Product) => {
    setSelectedProduct(product);
    setIsOpen(true);
  };

  const closeQuickAdd = () => {
    setIsOpen(false);
    // Clear product after animation completes
    setTimeout(() => setSelectedProduct(null), 300);
  };

  return {
    selectedProduct,
    isOpen,
    openQuickAdd,
    closeQuickAdd,
  };
}
